import { WebLayout } from 'components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import JonathanAvatarImg from 'assets/team/jonah.png';
import LonnahAvatarImg from 'assets/team/lonnah.png';
import IsikoAvatarImg from 'assets/team/isiko.png';
import KimAvatarImg from 'assets/team/kim.jpeg';
import KitsAvatarImg from 'assets/team/kits.png';

const teams = [
  {
    category: 'ADMINISTRATION',
    groups: [
      {
        title: 'Operations',
        description: 'Drives company vision, decision-making, strategy alignment.',
        members: [
          {
            name: 'Jonathan Mwebaze',
            role: 'CEO, Product Lead, Strategic Vision, Engineering',
            img: JonathanAvatarImg,
            quote:
              "At Ashita, I believe we're not just solving problems — we're shaping systems that make life easier and more meaningful. I spend most of my time thinking about how tech and design can align with human needs and long-term impact.",
          },
          {
            name: 'Lonnah Alinda Tumwesigye',
            role: 'COO, Operations & Workflow Management',
            img: LonnahAvatarImg,
            quote:
              'My focus is making sure the amazing ideas we come up with actually get delivered. Operations is about connecting all the moving parts and giving our team the space and structure to do their best work every day.',
          },

          {
            name: 'David Kyanda',
            role: 'Strategy Lead, Blockchain Vision & Systems',
            img: null,
            quote:
              'Blockchain gives us a unique opportunity to build trust into the foundation of our products. My role is to explore how decentralized tech can solve real-world problems in creative and useful ways.',
          },
          {
            name: 'Hakeem Kavuma',
            role: 'Legal & Partnerships, Investor Prep',
            img: KimAvatarImg,
            quote:
              'Startups move fast, but we can’t afford to be sloppy. I make sure we’re protected legally, structured for growth, and speaking the right language when it’s time to bring in partners or investors.',
          },
          {
            name: 'Josh Oola',
            role: 'Projects & Logistics Coordinator, Bidding & Field Rollout',
            img: null,
            quote:
              "I work where planning meets action — making sure timelines, logistics, and real-world execution come together seamlessly. Whether it's managing a rollout or prepping a pitch, I'm on it.",
          },
        ],
      },
    ],
  },
];

export const Team = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedMember, setSelectedMember] = useState(null);
  const modalRef = useRef(null);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedMember(null);
      }
    };
    if (selectedMember) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedMember]);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <WebLayout>
      <div className="w-full px-4 md:px-12 lg:px-24 py-10 bg-white dark:bg-gray-900 text-black dark:text-white relative">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold">Our Team of Creatives & Engineers</h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Visionaries. Engineers. Creators. Strategists.
          </p>
        </div>

        {teams.map((section, index) => (
          <div key={index} className="mb-20">
            {section.groups.map((group) => (
              <div key={group.title} className="mb-8">
                {/* <h3 className="text-xl font-medium mb-2">{group.title}</h3>
                <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">{group.description}</p> */}

                <motion.div
                  className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 transition-all duration-300 ${
                    selectedMember ? 'blur-sm pointer-events-none' : ''
                  }`}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {group.members.map((member, index) => (
                    <motion.div
                      key={index}
                      variants={cardVariants}
                      onClick={() => setSelectedMember(member)}
                      className="cursor-pointer bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition hover:scale-105"
                    >
                      <div className="w-full h-48 bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                        {member.img ? (
                          <img
                            src={member.img}
                            alt={member.name}
                            className="object-cover w-full h-full grayscale hover:grayscale-0 transition"
                          />
                        ) : (
                          <span className="text-3xl font-bold text-white">
                            {member.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div className="p-3 text-center">
                        <p className="font-semibold">{member.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{member.role}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* MODAL OVERLAY */}
                <AnimatePresence>
                  {selectedMember && (
                    <motion.div
                      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <motion.div
                        ref={modalRef}
                        className="bg-white dark:bg-gray-800 text-black dark:text-white w-full max-w-4xl mx-4 md:mx-0 rounded-xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        {/* IMAGE */}
                        <div className="w-full md:w-1/2 h-64 md:h-auto bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                          {selectedMember.img ? (
                            <img
                              src={selectedMember.img}
                              alt={selectedMember.name}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-6xl font-bold text-white">
                              {selectedMember.name.charAt(0)}
                            </span>
                          )}
                        </div>

                        {/* INFO */}
                        <div className="p-6 flex flex-col justify-between w-full md:w-1/2 space-y-4">
                          <div>
                            <h3 className="text-2xl font-bold">{selectedMember.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {selectedMember.role}
                            </p>
                            <p className="italic text-gray-700 dark:text-gray-300 text-md mb-2">
                              “{selectedMember.quote}”
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-400">
                              {selectedMember.bio}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            {selectedMember.email && (
                              <a
                                href={`mailto:${selectedMember.email}`}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                ✉ Email
                              </a>
                            )}
                            {selectedMember.linkedin && (
                              <a
                                href={selectedMember.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                🔗 LinkedIn
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => setSelectedMember(null)}
                            className="self-start text-sm mt-4 text-red-500 hover:underline"
                          >
                            ✕ Close
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        ))}
      </div>
    </WebLayout>
  );
};
